import React, { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, Search } from "lucide-react";
import ProductCard from "../components/Products/ProductCard";
import { productsService } from "../services/products";
import { optimizeImageUrl } from "../utils/images";

const PRODUCTS_PER_PAGE = 8;

const SORT_OPTIONS = [
  { value: "default", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A-Z" },
];

const HERO_IMAGE = optimizeImageUrl(
  "https://res.cloudinary.com/dirgb6nie/image/upload/v1775254817/e42d39e0-5f7a-4201-bad7-bc1e7e5b1054_yd1ycl.jpg",
  { width: 1600 }
);

function Skeleton() {
  return (
    <div className="rounded-sm overflow-hidden border border-gray-100 animate-pulse">
      <div className="bg-gray-200 aspect-[3/4]" />
      <div className="p-4">
        <div className="h-5 bg-gray-200 rounded mb-2 w-3/4" />
        <div className="h-3 bg-gray-100 rounded mb-4" />
        <div className="flex justify-between">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-8 bg-gray-200 rounded w-1/3" />
        </div>
      </div>
    </div>
  );
}

const PRODUCT_DETAILS = [
  {
    title: "Shipping Info",
    text: "Orders in Oyo typically arrive within 1 to 2 days, while nationwide delivery usually takes 3 to 5 days. Timelines may vary slightly during peak periods."
  },
  {
    title: "Return Policy",
    text: "If your order arrives with the wrong item or a fulfillment issue, contact support promptly with your order reference so we can review and help resolve it."
  },
];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("default");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let active = true;

    const loadProducts = async () => {
      const nextProducts = await productsService.fetchStorefrontProducts();
      if (!active) return;
      setProducts(nextProducts);
      setLoading(false);
    };

    loadProducts();

    const channel = productsService.subscribeToProducts(() => {
      loadProducts();
    });

    return () => {
      active = false;
      channel?.unsubscribe();
    };
  }, []);

  const categories = useMemo(() => {
    const available = Array.from(new Set(products.map((product) => product.category).filter(Boolean)));
    return ["All", ...available];
  }, [products]);

  const filtered = useMemo(() => {
    return [...products]
      .filter((product) => category === "All" || product.category === category)
      .filter((product) => {
        const query = search.trim().toLowerCase();
        return !query || product.name.toLowerCase().includes(query) || (product.description || "").toLowerCase().includes(query);
      })
      .sort((a, b) => {
        if (sort === "price-asc") return a.price - b.price;
        if (sort === "price-desc") return b.price - a.price;
        if (sort === "name-asc") return a.name.localeCompare(b.name);
        return 0;
      });
  }, [products, category, search, sort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [category, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = filtered.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="relative bg-brand-dark pt-36 pb-20 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
        />
        <div className="relative max-w-7xl mx-auto">
          <span className="section-label text-brand-red">Explore</span>
          <h1 className="font-display text-5xl sm:text-6xl text-white font-light mt-2">All Fragrances</h1>
          <p className="font-body text-white/45 text-sm mt-3 max-w-md">Discover our curated collection of authentic luxury perfumes.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-8">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search fragrances..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-sm font-body text-sm focus:outline-none focus:border-brand-red transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={14} className="text-gray-400 shrink-0" />
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="py-2.5 px-3 bg-white border border-gray-200 rounded-sm font-body text-sm focus:outline-none focus:border-brand-red cursor-pointer"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((item) => (
            <button
              key={item}
              onClick={() => setCategory(item)}
              className={`px-5 py-2 font-body text-[11px] uppercase tracking-widest rounded-sm border transition-all duration-200 ${
                category === item
                  ? "bg-brand-red text-white border-brand-red"
                  : "border-gray-300 text-gray-500 bg-white hover:border-brand-red hover:text-brand-red"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {!loading && (
          <p className="font-body text-xs text-gray-400 mb-6">
            Showing <span className="font-semibold text-brand-charcoal">{filtered.length}</span> fragrance{filtered.length !== 1 ? "s" : ""}
            {category !== "All" ? ` in ${category}` : ""}
          </p>
        )}

        <div className="mb-8 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-sm border border-gray-100 bg-white px-5 py-5">
            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-brand-red">Authenticity</p>
            <h2 className="font-display text-2xl text-brand-dark mt-3">Authentic product</h2>
            <p className="font-body text-sm text-gray-500 leading-relaxed mt-3">
              Every fragrance on this page is presented with a premium, confidence-first shopping experience built around secure checkout, fast delivery, and clear policies.
            </p>
          </div>

          <div className="rounded-sm border border-gray-100 bg-white overflow-hidden">
            {PRODUCT_DETAILS.map(({ title, text }) => (
              <details key={title} className="group border-b border-gray-100 last:border-b-0">
                <summary className="list-none cursor-pointer px-5 py-4 flex items-center justify-between font-body text-sm text-brand-charcoal">
                  <span>{title}</span>
                  <span className="text-brand-red transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="font-body text-sm text-gray-500 leading-relaxed">{text}</p>
                </div>
              </details>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: PRODUCTS_PER_PAGE }, (_, index) => (
              <Skeleton key={index} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-28">
            <p className="font-display text-2xl text-gray-300">No fragrances found</p>
            <p className="font-body text-sm text-gray-400 mt-2">Try a different search term or category</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {paginatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-2">
                <button
                  onClick={() => handlePageChange(safeCurrentPage - 1)}
                  disabled={safeCurrentPage === 1}
                  className="min-w-[110px] px-4 py-2.5 border border-gray-300 bg-white text-sm font-body text-brand-charcoal rounded-sm transition-colors hover:border-brand-red hover:text-brand-red disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-brand-charcoal"
                >
                  Previous
                </button>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  {pageNumbers.map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      aria-current={page === safeCurrentPage ? "page" : undefined}
                      className={`w-10 h-10 rounded-sm border text-sm font-body transition-colors ${
                        page === safeCurrentPage
                          ? "bg-brand-red border-brand-red text-white"
                          : "bg-white border-gray-300 text-brand-charcoal hover:border-brand-red hover:text-brand-red"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(safeCurrentPage + 1)}
                  disabled={safeCurrentPage === totalPages}
                  className="min-w-[110px] px-4 py-2.5 border border-gray-300 bg-white text-sm font-body text-brand-charcoal rounded-sm transition-colors hover:border-brand-red hover:text-brand-red disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-brand-charcoal"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
